import {
    useCallback,
    useEffect,
    useRef,
    useState,
    type KeyboardEvent,
    type MutableRefObject,
} from 'react';
import { useTextareaAutoSize } from '../../hooks/useTextareaAutoSize';

export type SlotDraftMode = 'idle' | 'slash' | 'text';
const WINDOWS_NEWLINE_PATTERN = /\r\n/g;

interface UseAddElementSlotFreeTextDraftArgs {
    enabled: boolean;
    onCommit: (text: string) => void;
    onDraftTextChange?: (draftText: string) => void;
    onDraftReset?: () => void;
}

interface UseAddElementSlotFreeTextDraftResult {
    inputValue: string;
    draftMode: SlotDraftMode;
    shouldOpenSlashMenu: boolean;
    textareaRef: MutableRefObject<HTMLTextAreaElement | null>;
    resetDraft: () => void;
    handleDraftValueChange: (nextValue: string) => void;
    handleDraftKeyDown: (event: KeyboardEvent<HTMLTextAreaElement>) => void;
    handleDraftBlur: () => void;
}

export function useAddElementSlotFreeTextDraft({
    enabled,
    onCommit,
    onDraftTextChange,
    onDraftReset,
}: UseAddElementSlotFreeTextDraftArgs): UseAddElementSlotFreeTextDraftResult {
    const [inputValue, setInputValue] = useState('');
    const [draftMode, setDraftMode] = useState<SlotDraftMode>('idle');
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);
    const inputValueRef = useRef('');
    const draftModeRef = useRef<SlotDraftMode>('idle');

    const resetDraft = useCallback((): void => {
        inputValueRef.current = '';
        setInputValue('');
        draftModeRef.current = 'idle';
        setDraftMode('idle');
        onDraftReset?.();
    }, [onDraftReset]);

    const commitDraftIfNonEmpty = useCallback((): void => {
        const draftText = inputValueRef.current.replace(WINDOWS_NEWLINE_PATTERN, '\n');
        if (!draftText.trim().length) {
            return;
        }

        onCommit(draftText);
        resetDraft();
    }, [onCommit, resetDraft]);

    function handleDraftValueChange(nextValue: string): void {
        const currentMode = draftModeRef.current;

        if (!nextValue.length) {
            resetDraft();
            return;
        }

        if (currentMode === 'idle') {
            const nextMode = nextValue.startsWith('/') ? 'slash' : 'text';
            draftModeRef.current = nextMode;
            setDraftMode(nextMode);
            inputValueRef.current = nextValue;
            setInputValue(nextValue);
            if (!nextValue.startsWith('/')) {
                onDraftTextChange?.(nextValue);
            }
            return;
        }

        if (currentMode === 'slash' && !nextValue.startsWith('/')) {
            draftModeRef.current = 'text';
            setDraftMode('text');
            onDraftTextChange?.(nextValue);
            inputValueRef.current = nextValue;
            setInputValue(nextValue);
            return;
        }

        inputValueRef.current = nextValue;
        setInputValue(nextValue);
        if (currentMode === 'text') {
            onDraftTextChange?.(nextValue);
        }
    }

    function handleDraftKeyDown(event: KeyboardEvent<HTMLTextAreaElement>): void {
        const shouldCommitWithShift = event.key === 'Enter' && event.shiftKey;
        if (shouldCommitWithShift && draftModeRef.current === 'text') {
            event.preventDefault();
            commitDraftIfNonEmpty();
        }
    }

    function handleDraftBlur(): void {
        if (draftModeRef.current === 'text') {
            commitDraftIfNonEmpty();
        }
    }

    useTextareaAutoSize(textareaRef, inputValue, enabled);

    useEffect(() => {
        if (!enabled) {
            return undefined;
        }

        function handleSubmitCapture(event: Event): void {
            if (draftModeRef.current === 'text') {
                const submitForm = event.target;
                const ownerForm = textareaRef.current?.form;
                if (
                    submitForm instanceof HTMLFormElement &&
                    ownerForm instanceof HTMLFormElement &&
                    submitForm !== ownerForm
                ) {
                    return;
                }

                commitDraftIfNonEmpty();
            }
        }

        window.addEventListener('submit', handleSubmitCapture, true);
        return () => {
            window.removeEventListener('submit', handleSubmitCapture, true);
        };
    }, [enabled, commitDraftIfNonEmpty]);

    return {
        inputValue,
        draftMode,
        shouldOpenSlashMenu: enabled && draftMode === 'slash',
        textareaRef,
        resetDraft,
        handleDraftValueChange,
        handleDraftKeyDown,
        handleDraftBlur,
    };
}
